import {
  useConfirmNewBookingAccountCreationMutation,
  useGetNewBookingAccountDetailsQuery,
} from '../../../../user_profile/userProfileSlice';
import React, { useCallback, useState } from 'react';
import { useAccountCreation } from '../../../../../utils/hooks';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import BookingNewAccountConfirmation from './BookingAccountConfirmation';
import {
  AccountAlreadyActiveMessage,
  AccountCreationErrorMessage,
  AccountCreationForm,
  NewLinkSentMessage,
} from '../../../../signup/components/AccountCreationComponents';

export default function UserBookingNewAccountCreationForm({
  token,
  userId,
  bookingId,
  header = null,
  hideFieldLabels = false,
  useTrialConfirmation = false,
  onFinish = () => {},
}) {
  const {
    data: accountDetails,
    isFetching: loadingDetails,
    error,
  } = useGetNewBookingAccountDetailsQuery(
    { userId: userId, bookingId: bookingId },
    { skip: !userId }
  );

  const [
    confirmAccountCreation,
    { isLoading: confirmingCreation, error: confirmationError },
  ] = useConfirmNewBookingAccountCreationMutation();
  const [showConfirmationPage, setShowConfirmationPage] = useState(false);

  const accountAlreadyActive = Boolean(accountDetails?.user?.is_active);
  const userEmail = accountDetails?.user?.email;

  const [
    resetPasswordAndLogin,
    {
      isLoading,
      isSubmitting,
      isTokenValid,
      isResetTriggered,
      submissionError,
    },
  ] = useAccountCreation({
    email: userEmail,
    token,
    alreadyActive: accountAlreadyActive,
  });

  const submitting = isSubmitting || confirmingCreation;
  const loading = loadingDetails || isLoading;

  const onFormSubmit = useCallback(
    ({ email, password }) => {
      resetPasswordAndLogin(email, password)
        .then(() => confirmAccountCreation({ userID: userId }).unwrap())
        .then(() => {
          onFinish();
          setShowConfirmationPage(true);
        });
    },
    [confirmAccountCreation, onFinish, resetPasswordAndLogin, userId]
  );

  if (loading) {
    return (
      <div
        className='grid-center txt-center pd-10 h-full'
        style={{ height: 500 }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  if (showConfirmationPage) {
    return (
      <BookingNewAccountConfirmation
        useTrialConfirmation={useTrialConfirmation}
      />
    );
  }

  if (accountAlreadyActive) {
    return <AccountAlreadyActiveMessage className='pd-10 h-full' />;
  }

  if (isResetTriggered) {
    return <NewLinkSentMessage className='pd-10 h-full' />;
  }

  if (
    error ||
    submissionError ||
    confirmationError ||
    !userId ||
    !isTokenValid
  ) {
    return (
      <AccountCreationErrorMessage
        className='pd-20 pd-bottom-30 h-full'
        message={
          (!userId || !isTokenValid
            ? `Invalid Link`
            : (error || submissionError)?.data?.detail) ??
          'Something went wrong!'
        }
        offerNewLink={!isTokenValid}
      />
    );
  }

  return (
    <div className='h-full'>
      {header}

      <AccountCreationForm
        userEmail={userEmail}
        onFormSubmit={onFormSubmit}
        submitDisabled={submitting}
        hideFieldLabels={hideFieldLabels}
        emailFieldClass={'grey-bg-300'}
        fieldClass='br10 border-0 fs-16'
        passwordText={'Password'}
        buttonText={'Finish Account Setup'}
        buttonClass='sb-blue-gradient-bg-right border-1 sb-blue-border-300 mg-top-10 white gSemiBold brFull fs-18 txt-center w-full max-w-300 pd-10 h-fit txt-wrap antd-link-button-override'
      />
    </div>
  );
}
